import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaFacebookF, FaInstagram
} from 'react-icons/fa';
import {
  HiEnvelope, HiPhone, HiMapPin, HiArrowUpRight
} from 'react-icons/hi2';

const footerLinks = {
  quickLinks: [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About Us' },
    { to: '/telecom', label: 'Telecom Services' },
    { to: '/gallery', label: 'Gallery' },
    { to: '/clients', label: 'Industries We Serve' },
    { to: '/careers', label: 'Careers' },
    { to: '/contact', label: 'Contact Us' },
  ],
  divisions: [
    { to: '/generator-solutions-overview', label: 'Generator Solutions' },
    { to: '/solar-solutions', label: 'Solar Power Solutions' },
    { to: '/electrical-panels', label: 'Electrical Control Panels' },
    { to: '/cutting-tools', label: 'Industrial Cutting Tools' },
  ],
  gensetServices: [
    { to: '/generator-sales', label: 'Generator Sales' },
    { to: '/generator-rental', label: 'Generator Rentals' },
    { to: '/generator-maintenance', label: 'Generator Maintenance & AMC' },
  ],
};

export default function Footer() {
  return (
    <footer className="text-white/60 font-sans relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #0C1A12 0%, #081008 100%)' }}>

      {/* Decorative gradient elements */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-light/30 to-transparent" />
      <div className="absolute top-0 left-1/4 w-64 h-64 bg-primary/8 rounded-full blur-[80px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-accent/5 rounded-full blur-[60px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-10 relative z-10">

        {/* ---- Main Footer Grid ---- */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 mb-16">

          {/* Column 1: Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6 group">
              <div className="bg-white rounded-2xl p-3 shadow-premium w-fit transition-all duration-300 group-hover:shadow-glow-green">
                <img
                  src="/Green Gusto.png"
                  alt="Green Gusto Logo"
                  className="h-14 w-auto object-contain"
                />
              </div>
            </Link>
            <p className="text-sm text-white/50 leading-relaxed mb-6 max-w-sm">
              Green Gusto Energy Pvt. Ltd. is a premier industrial power solutions provider, specializing in generator sales, rentals, telecom operations, and AMC/CAMC maintenance contracts.
            </p>

            {/* Social Icons */}
            <div className="flex items-center gap-3 mb-8">
              {[
                { icon: <FaFacebookF className="w-3.5 h-3.5" />, href: 'https://www.facebook.com/greengusto', label: 'Facebook' },
                { icon: <FaInstagram className="w-3.5 h-3.5" />, href: 'https://www.instagram.com/greengusto_pvt.ltd/', label: 'Instagram' },
              ].map(({ icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-9 h-9 rounded-xl flex items-center justify-center
                             text-white/50 hover:text-accent hover:border-accent/20
                             transition-all duration-200"
                  style={{
                    background: 'rgba(28, 140, 91, 0.06)',
                    border: '1px solid rgba(28, 140, 91, 0.10)',
                  }}
                >
                  {icon}
                </a>
              ))}
            </div>

            {/* Contact Quick Info */}
            <div className="space-y-3">
              <a
                href="tel:+919609600600"
                className="flex items-center gap-3 text-sm text-white/50 hover:text-accent transition-colors group"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-primary-light flex-shrink-0" style={{ background: 'rgba(28, 140, 91, 0.10)', border: '1px solid rgba(28, 140, 91, 0.15)' }}>
                  <HiPhone className="w-3.5 h-3.5" />
                </div>
                <span>+91 96096 00600</span>
              </a>
              <a
                href="mailto:info@green-gusto.com"
                className="flex items-center gap-3 text-sm text-white/50 hover:text-accent transition-colors group"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-primary-light flex-shrink-0" style={{ background: 'rgba(28, 140, 91, 0.10)', border: '1px solid rgba(28, 140, 91, 0.15)' }}>
                  <HiEnvelope className="w-3.5 h-3.5" />
                </div>
                <span>info@green-gusto.com</span>
              </a>
              <div className="flex items-start gap-3 text-sm text-white/50">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-primary-light flex-shrink-0 mt-0.5" style={{ background: 'rgba(28, 140, 91, 0.10)', border: '1px solid rgba(28, 140, 91, 0.15)' }}>
                  <HiMapPin className="w-3.5 h-3.5" />
                </div>
                <span>Plot No. B-15/2/67 and B-15/2/68, Waluj MIDC Industrial Area, Near Jonson & Jonsons, Tal. Gangapur, Dist. Chhatrapati Sambhajinagar - 431136, Maharashtra</span>
              </div>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-5 relative">
              Quick Links
              <span className="absolute -bottom-2 left-0 w-8 h-0.5 rounded-full" style={{ background: 'linear-gradient(90deg, #0F5A3A, #1C8C5B)' }} />
            </h4>
            <ul className="space-y-3 mt-4">
              {footerLinks.quickLinks.map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className="footer-link">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Business Divisions */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-5 relative">
              Business Divisions
              <span className="absolute -bottom-2 left-0 w-8 h-0.5 rounded-full" style={{ background: 'linear-gradient(90deg, #0F5A3A, #1C8C5B)' }} />
            </h4>
            <ul className="space-y-3 mt-4">
              {footerLinks.divisions.map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className="footer-link">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Genset Services */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-5 relative">
              Genset Services
              <span className="absolute -bottom-2 left-0 w-8 h-0.5 rounded-full" style={{ background: 'linear-gradient(90deg, #0F5A3A, #1C8C5B)' }} />
            </h4>
            <ul className="space-y-3 mt-4">
              {footerLinks.gensetServices.map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className="footer-link">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* CTA Card */}
            <div className="mt-8 p-5 rounded-2xl border border-white/6" style={{ background: 'linear-gradient(135deg, rgba(15, 90, 58, 0.25) 0%, rgba(28, 140, 91, 0.12) 100%)' }}>
              <p className="text-xs text-white/60 mb-4 leading-relaxed">
                Need emergency power backup?
              </p>
              <Link
                to="/contact#enquiry"
                className="inline-flex items-center gap-2 text-xs font-semibold text-accent hover:text-white transition-colors"
              >
                Get a Free Quote
                <HiArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>

        {/* ---- Footer Bottom Bar ---- */}
        <div className="border-t border-white/6 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/35 text-center md:text-left">
            &copy; {new Date().getFullYear()} Green Gusto Energy Pvt. Ltd. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs text-white/35">
            <span>
              Designed &amp; Developed by{' '}
              <a
                href="https://sahyadritech.in/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-white/55 hover:text-accent transition-colors"
              >
                Sahyadri Tech
              </a>
            </span>
            <span className="text-white/15">|</span>
            <span>MSME Registered</span>
            <span className="text-white/15">|</span>
            <span>ISO 9001:2015</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
